import { createContext, useCallback, useContext, useMemo, useRef, type ReactNode } from 'react'
import type { MessagingModule } from '../components/messaging-channel-tabs'

type ReselectHandler = (module: MessagingModule) => void

type MessagingMobileNavContextValue = {
  registerReselectHandler: (handler: ReselectHandler | null) => void
  onModuleReselect: (module: MessagingModule) => void
}

const MessagingMobileNavContext = createContext<MessagingMobileNavContextValue | null>(null)

export function MessagingMobileNavProvider({ children }: { children: ReactNode }) {
  const handlerRef = useRef<ReselectHandler | null>(null)

  const registerReselectHandler = useCallback((handler: ReselectHandler | null) => {
    handlerRef.current = handler
  }, [])

  const onModuleReselect = useCallback((module: MessagingModule) => {
    handlerRef.current?.(module)
  }, [])

  const value = useMemo(
    () => ({ registerReselectHandler, onModuleReselect }),
    [registerReselectHandler, onModuleReselect],
  )

  return (
    <MessagingMobileNavContext.Provider value={value}>{children}</MessagingMobileNavContext.Provider>
  )
}

export function useMessagingMobileNav(): MessagingMobileNavContextValue {
  const ctx = useContext(MessagingMobileNavContext)
  if (!ctx) {
    throw new Error('useMessagingMobileNav must be used within MessagingMobileNavProvider')
  }
  return ctx
}

/** Optional hook for pages that may render outside the provider (e.g. announcements). */
export function useMessagingMobileNavOptional(): MessagingMobileNavContextValue | null {
  return useContext(MessagingMobileNavContext)
}
